import { useEffect, useMemo, useState } from "react";
import useCustomerContext from "Contexts/customer-context";
import useUserContext from "Contexts/user-context";
import { PayoutInformationBankConsent } from "Shared/util/types/api";
import { getPayoutInformation } from "Shared/util/web-api";
import { customerIsValidForBanners } from "../utils";

type FetchPayoutInformationCallback = (
    data: PayoutInformationBankConsent[] | null,
) => void;

export const fetchPayoutInformation = async (
    activeCompanyId: number | undefined,
    onDataFetched?: FetchPayoutInformationCallback,
): Promise<PayoutInformationBankConsent[] | null> => {
    if (!activeCompanyId) return null;

    try {
        const result = await getPayoutInformation();
        const bankConsents = result?.bankConsents ?? null;
        onDataFetched?.(bankConsents);
        return bankConsents;
    } catch (error) {
        console.error("Failed to fetch payout information:", error);
        return null;
    }
};

export const useGetDataForPayoutInformationBanners = () => {
    const { user } = useUserContext();
    const { customerData } = useCustomerContext();

    const [isLoading, setIsLoading] = useState(true);
    const [bankConsents, setBankConsents] = useState<
        PayoutInformationBankConsent[] | null
    >(null);

    const { id: activeCompanyId, role } = user?.activeCompany || {};
    const isAdminUser = !!role?.isAdmin;

    useEffect(() => {
        if (!activeCompanyId) return;

        setIsLoading(true);
        fetchPayoutInformation(activeCompanyId, (data) => {
            setBankConsents(data);
            setIsLoading(false);
        });
    }, [activeCompanyId]);

    const shouldNotRenderBanners = useMemo(
        () =>
            isLoading ||
            !isAdminUser ||
            !bankConsents?.length ||
            !customerIsValidForBanners(customerData),
        [isLoading, isAdminUser, bankConsents, customerData],
    );

    return {
        bankConsents,
        customerData,
        shouldNotRenderBanners,
        role,
        isAdminUser,
    };
};
